import { AlertTriangle, CircleAlert, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { flagLabels, stateLabels, syncLabels } from '@/lib/labels'
import type { Flag, SubscriptionStateName } from '@/lib/types'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const pill = 'inline-flex h-5 items-center gap-1 rounded-full px-2 text-xs font-medium whitespace-nowrap'

/** `trial`: the plan is the new team's trial, not one it bought or was given. */
export function PlanBadge({ planId, name, trial = false }: { planId: string; name: string; trial?: boolean }) {
  const paid = planId !== 'free' && !trial
  return (
    <span className={cn(pill, paid ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground')}>
      {trial ? `${name.replace(/ (monthly|yearly)$/i, '')} · trial` : name.replace('Premium ', 'Premium · ')}
    </span>
  )
}

const stateTone: Record<SubscriptionStateName, string> = {
  free: 'text-muted-foreground',
  active: 'text-ok',
  expiring: 'text-warn',
  canceling: 'text-warn',
  expired: 'text-risk',
}

/** A coloured dot and a word: the state of a subscription. */
export function StateBadge({ state }: { state: SubscriptionStateName }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', stateTone[state])}>
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {stateLabels[state]}
    </span>
  )
}

const flagTone = {
  risk: 'bg-risk/10 text-risk',
  warn: 'bg-warn/12 text-warn',
  quiet: 'bg-secondary text-muted-foreground',
}

export function FlagChip({ flag }: { flag: Flag }) {
  const { label, hint, tone } = flagLabels[flag]
  const Icon = tone === 'risk' ? CircleAlert : tone === 'warn' ? AlertTriangle : Clock
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(pill, flagTone[tone])}>
          <Icon className="size-3" aria-hidden />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  )
}

/** The first `max` flags, then a count for the rest. */
export function FlagChips({ flags, max = 2 }: { flags: Flag[]; max?: number }) {
  if (flags.length === 0) return <span className="text-muted-foreground">—</span>
  const rest = flags.slice(max)
  return (
    <div className="flex flex-wrap items-center gap-1">
      {flags.slice(0, max).map((f) => (
        <FlagChip key={f} flag={f} />
      ))}
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(pill, 'bg-secondary text-muted-foreground')}>+{rest.length}</span>
          </TooltipTrigger>
          <TooltipContent>{rest.map((f) => flagLabels[f].label).join(', ')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

const syncTone: Record<string, string> = {
  success: 'bg-ok',
  in_progress: 'bg-primary',
  error: 'bg-risk',
  not_started: 'bg-muted-foreground/50',
}

export function SyncStatus({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('size-1.5 rounded-full', syncTone[status] ?? 'bg-muted-foreground/50')} aria-hidden />
      {syncLabels[status] ?? status}
    </span>
  )
}

export function RoleBadge({ role }: { role: 'admin' | 'user' }) {
  return (
    <span className={cn(pill, role === 'admin' ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground')}>
      {role === 'admin' ? 'Admin' : 'Member'}
    </span>
  )
}

const invoiceTone: Record<string, string> = {
  paid: 'text-ok',
  open: 'text-warn',
  uncollectible: 'text-risk',
  void: 'text-muted-foreground',
  draft: 'text-muted-foreground',
}

export function InvoiceStatus({ status, attempts }: { status: string; attempts?: number }) {
  const failed = status === 'open' && (attempts ?? 0) > 0
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', failed ? 'text-risk' : (invoiceTone[status] ?? ''))}>
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {failed ? 'Payment failed' : status === 'uncollectible' ? 'Written off' : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}
